import React, { useId } from 'react'

const parseColorToRgb = (value) => {
  if (!value || typeof value !== 'string') return null

  const color = value.trim().toLowerCase()

  if (color.startsWith('#')) {
    const hex = color.slice(1)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16)
      const g = parseInt(hex[1] + hex[1], 16)
      const b = parseInt(hex[2] + hex[2], 16)
      return { r, g, b }
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return { r, g, b }
    }
    return null
  }

  const rgbMatch = color.match(/rgba?\(([^)]+)\)/)
  if (!rgbMatch) return null

  const channels = rgbMatch[1]
    .split(',')
    .map((part) => Number.parseFloat(part.trim()))
    .filter((num) => Number.isFinite(num))

  if (channels.length < 3) return null

  return {
    r: Math.max(0, Math.min(255, Math.round(channels[0]))),
    g: Math.max(0, Math.min(255, Math.round(channels[1]))),
    b: Math.max(0, Math.min(255, Math.round(channels[2])))
  }
}

const getPerceptualLuminance = ({ r, g, b }) => (0.2126 * r) + (0.7152 * g) + (0.0722 * b)
const toRgba = ({ r, g, b }, alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`

const AnimatedGradientBackground = ({
  className = "",
  colors = null, // Expected format: { gradientColors: ['#color1', '#color2', '#color3', '#color4', '#color5'], blobColors: ['#color6', '#color7'] }
  brightness = 100, // 0-100% brightness
  animationPaused = false, // Whether to pause animations
  hardEdges = false, // Debug mode: crisp blob edges
  mergeNearbyBlobs = true, // Metaball-style merge when blobs are near each other
}) => {
  // Default colors if none provided
  const defaultGradientColors = ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050']
  const defaultBlobColors = ['#e51e25', '#e51e25']
  const gooFilterId = useId().replace(/:/g, '')

  const gradientColors = colors?.gradientColors || defaultGradientColors
  const blobColors = colors?.blobColors || defaultBlobColors
  const parsedPaletteColors = [...gradientColors, ...blobColors]
    .map(parseColorToRgb)
    .filter(Boolean)
  const darkestColor = parsedPaletteColors.length
    ? parsedPaletteColors.reduce((darkest, current) => (
      getPerceptualLuminance(current) < getPerceptualLuminance(darkest) ? current : darkest
    ))
    : { r: 11, g: 15, b: 27 }
  const gooMatrixValues = hardEdges
    ? '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -12'
    : '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 16 -7'
  const layerFilter = mergeNearbyBlobs
    ? `url(#${gooFilterId}) ${hardEdges ? '' : 'blur(2vw)'} saturate(120%) contrast(105%) brightness(110%)`
    : 'saturate(120%) contrast(105%) brightness(110%)'

  return (
    <div
      className={`animated-gradient-wrapper ${className} ${animationPaused ? 'paused' : ''}`}
      style={{
        opacity: brightness / 100,
        '--ambient-color-1': gradientColors[1] || defaultGradientColors[1],
        '--ambient-color-2': gradientColors[3] || defaultGradientColors[3],
        '--blob-color-1': blobColors[0] || gradientColors[0] || defaultBlobColors[0],
        '--blob-color-2': blobColors[1] || gradientColors[4] || defaultBlobColors[1],
        '--blob-color-3': gradientColors[2] || gradientColors[0] || defaultGradientColors[2],
        '--blob-color-4': gradientColors[0] || defaultGradientColors[0],
        '--blob-color-5': gradientColors[4] || defaultGradientColors[4],
        '--ambient-dark-color-soft': toRgba(darkestColor, 0.74),
        '--ambient-dark-color-deep': toRgba(darkestColor, 0.96)
      }}
    >
      {mergeNearbyBlobs && (
        <svg className="absolute w-0 h-0 pointer-events-none" aria-hidden="true" focusable="false">
          <defs>
            <filter id={gooFilterId} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation={hardEdges ? 50 : 80} result="blobBlur" />
              <feColorMatrix
                in="blobBlur"
                mode="matrix"
                values={gooMatrixValues}
                result="blobGoo"
              />
              <feComposite in="SourceGraphic" in2="blobGoo" operator="atop" />
            </filter>
          </defs>
        </svg>
      )}
      <div className="organic-bg-base"></div>
      <div
        className={`organic-bg-layer ${hardEdges ? 'hard-edges' : ''}`}
        style={{ filter: layerFilter }}
      >
        <div className="organic-blob blob-1"></div>
        <div className="organic-blob blob-2"></div>
        <div className="organic-blob blob-3"></div>
        <div className="organic-blob blob-4"></div>
        <div className="organic-blob blob-5"></div>
      </div>
      <div className="organic-bg-vignette"></div>
    </div>
  )
}

export default AnimatedGradientBackground
